<template src="./instant-answers.html"></template>

<script lang="ts">
import { defineAsyncComponent, nextTick } from 'vue'
import Ask from 'components/ask/Ask.vue'
import createQuestionAnonOrUnconfirmedUserService
  from 'services/api/create-question-anon-or-unconfirmed-user'
import createQuestionConfirmedUser from 'services/api/create-question-confirmed-user'
import domUtils from 'util/dom-utils'
import { debounce } from 'util/ts-utils'
import { getOfflineOrUnexpectedMsg } from 'services/msg'
import globalConfig from 'init/global.config'
import globalStore, { Store } from 'init/global-store'
import { hostsConfig } from 'init/config'
import iaConfig from 'widgets/instant-answers/instant-answers.config'
import instantAnswerSearchService from 'services/api/instant-answer-search'
import MultiItems from 'components/question/MultiItems.vue'
import qaConfig, { RelatedQuestionsDisplayTypes } from 'widgets/qa/qa.config'
import { RestError } from 'services/api-service'
import reviewsListConfig, { RelatedReviewsDisplayTypes }
  from 'widgets/reviews-list/reviews-list-widget.config'
import ssoService from 'services/sso-service'
import submissionLoader from 'widgets/submission/submission-loader'
import submissionStore from 'store/submission-store'
import tokenService from 'services/token-service'
import UserToolbar from 'components/user-toolbar/UserToolbar.vue'
import { containsEmail, containsPhoneNumber } from 'util/validation-utils'
import trackingService from 'services/tracking-service'

import 'components/ask-results/ask-results.scss'
import visibleObserver from 'util/visible-observer'

const MAX_CHARACTERS = 2000
const CHARACTER_THRESHOLD = 200

export default {
  name: 'InstantAnswers',
  data() {
    return {
      detailItem: null,
      detailItemType: null,
      globalStoreState: globalStore.state,
      inAskMode: false, // is true when user has clicked button to submit new question
      isOffline: false,
      isSearching: false,
      isSubmitting: false,
      errorText: null,
      multiItems: [],
      previousQuestionText: '',
      questionText: '',
      searchResults: []
    }
  },
  components: {
    'tt-ask': Ask,
    'tt-multi-items': MultiItems,
    'tt-ask-results-content': defineAsyncComponent(
      // eslint-disable-next-line max-len
      () => import(/* webpackChunkName: 'ask-results-content' */ 'components/ask-results/AskResultsContent.vue')
    ),
    'tt-user-toolbar': UserToolbar
  },
  computed: {
    charactersLeft() {
      return MAX_CHARACTERS - this.trimmedQuestionText.length
    },
    countText(): string {
      return (this.charactersLeft <= CHARACTER_THRESHOLD) ?
        this.$msg('cc.charactersLeft', [this.charactersLeft]) : ''
    },
    currentSku(): string {
      return (this.globalStoreState as Store).sku
    },
    multiItemsPromptText() {
      return this.$msg(`multiItem.question.prompt${this.multiItems.length > 0 ? '.withAdded' : ''}`)
    },
    resultsPromptText() {
      return this.searchResults.length > 0 ? this.$msg('ia.results.notWhatLookingFor') :
        this.$msg('ia.results.reviseSearch')
    },
    resultsCountText() {
      if (this.detailItem || this.inAskMode || !this.trimmedQuestionText) {
        return ''
      }
      const resultsCount = this.searchResults.length
      if (resultsCount === 0) {
        return this.isOffline ? this.$msg('offline.error.search')
          : this.$msg('askResults.count.none')
      }
      return this.$msg('askResults.count', [resultsCount], resultsCount)
    },
    showMultiItems() {
      return iaConfig.multiItemQuestionEnabled && this.inAskMode
    },
    showResultsContent() {
      return this.trimmedQuestionText && !this.inAskMode
    },
    trimmedQuestionText() {
      return this.questionText.trim()
    }
  },
  methods: {
    addMultiItems(items) {
      items.forEach((item) => {
        if (this.multiItems.filter(mi => mi.id === item.id).length === 0) {
          this.multiItems.push(item)
        }
      })
    },
    askUpdate() {
      const trimmedText = this.trimmedQuestionText
      // don't want to trigger unnecessary searces
      if (trimmedText === this.previousQuestionText) {
        return
      }
      this.previousQuestionText = trimmedText
      if (this.charactersLeft < 0) {
        this.errorText = this.$msg('cc.charactersOver', [Math.abs(this.charactersLeft)])
        return
      }
      this.clearErrors()
      if (trimmedText.length === 0) {
        this.inAskMode = false
        this.searchResults = []
      } else if (!this.inAskMode) {
        this.instantAnswerSearch()
      }
    },
    backToResults() {
      const listItemId = `tt-ari-${this.detailItemType}-${this.detailItem.id}`
      this.onDetailItemChange(null, null)
      // focus on the list item that the user clicked on to get to the detail view
      // timeout is to wait for the animation to complete.  nextTick not cutting it
      setTimeout(() => {
        domUtils.safeFocus(() => document.getElementById(listItemId))
      }, 400)
    },
    cancelAskMode() {
      this.inAskMode = false
      this.questionText = ''
      this.clearErrors()
    },
    clearErrors() {
      this.errorText = ''
    },
    enterAskMode() {
      this.inAskMode = true
      this.onDetailItemChange(null, null)
      domUtils.safeFocusRef(this.$refs, 'submitBtn').catch(() => {})
    },
    instantAnswerSearch: debounce(function () { // eslint-disable-line func-names
      this.isSearching = true
      this.isOffline = false
      instantAnswerSearchService.instantAnswerSearch({
        catalogItemId: globalStore.catItemId,
        categoryIds: globalStore.parentCategoryIds,
        brand: globalStore.brand,
        includeRelatedQuestions:
          qaConfig.relatedQuestionsDisplay === RelatedQuestionsDisplayTypes.INCLUDED,
        includeRelatedReviews:
          reviewsListConfig.relatedReviewsDisplay === RelatedReviewsDisplayTypes.INCLUDED,
        searchTerm: this.trimmedQuestionText,
        vpcId: globalStore.vpcId
      }).then((data) => {
        this.searchResults = data.items
        this.isSearching = false

        trackingService.queueEvent('instant-answers', 'search', this.currentSku, {
          nr: this.searchResults.length,
          s: this.trimmedQuestionText
        })
      }).catch((e: RestError) => {
        if (e.errorCode === 'turnto-offline') {
          this.isOffline = true
        }
        this.isSearching = false
      })
    }, 300),
    onDetailItemChange(itemData, type) {
      this.detailItem = itemData
      this.detailItemType = type
    },
    onPageChange(pageNum) {
      nextTick(() => {
        domUtils.safeFocusRef(this.$refs, 'resultsCount').catch(() => {})
      })
      trackingService.queueEvent('instant-answers', 'page', this.currentSku, {
        nr: this.searchResults.length,
        s: this.trimmedQuestionText,
        p: pageNum
      })
    },
    onShowError(e) {
      if (e.fieldErrors.length > 0 && e.fieldErrors[0].code === 'ext-site-link') {
        this.errorText = this.$msg('ia.err.containsLink')
        this.isSubmitting = false
      } else {
        // used for error like question submission where modal not open
        import(/* webpackChunkName: 'standalone-popover' */ 'widgets/standalone-popover/standalone-popover-loader').then((popoverLoader) => {
          popoverLoader.default.load({
            message: getOfflineOrUnexpectedMsg(e), type: 'error', autoClose: true
          })
        })
        this.resetAfterError()
      }
    },
    removeMultiItem(item) {
      for (let i = 0; i < this.multiItems.length; i++) {
        if (this.multiItems[i].id === item.id) {
          this.multiItems.splice(i, 1)
          break
        }
      }
    },
    resetAfterError() {
      // so we don't wipe out question giving them option of trying again
      // which may only be practical for offline error case
      this.inAskMode = false
      this.isSearching = false
      this.isSubmitting = false
      this.isOffline = false
    },
    resetAfterSubmit() {
      this.cancelAskMode()
      this.isSearching = false
      this.isSubmitting = false
      this.isOffline = false
      this.searchResults = []
      this.multiItems = []
    },
    validateQuestion(text) {
      this.clearErrors()
      if (this.charactersLeft < 0) {
        this.errorText = this.$msg('cc.charactersOver', [Math.abs(this.charactersLeft)])
        return false
      }
      if (text.search(/[?]/im) === -1) {
        this.errorText = this.$msg('ia.error.isThisAQuestion')
        return false
      }
      if (containsEmail(text)) {
        this.errorText = this.$msg('ia.err.dontIncludeEmail')
        return false
      }
      if (containsPhoneNumber(text)) {
        this.errorText = this.$msg('ia.err.dontIncludePhone')
        return false
      }
      return true
    },
    submitQuestion() {
      this.isSubmitting = true
      if (!this.validateQuestion(this.trimmedQuestionText)) {
        const field = this.$el.querySelector('.tt-o-search-field__input')
        domUtils.focus(field).then(() => {
          // Safari was having an issue focusing at end of content in textarea.
          // Most likely because it's a contenteditable div instead of an actual textarea.
          domUtils.setCursorAtEndOfText(field)
        }).catch(() => {})
        this.isSubmitting = false
        return
      }
      const userToken = tokenService.getUserToken()
      if (userToken) {
        // This is a logged in, and therefore confirmed user.
        createQuestionConfirmedUser.createQuestionConfirmedUser({
          'context-token': userToken,
          sku: this.currentSku
        }, {
          questionType: 0,
          text: this.trimmedQuestionText,
          multiItemIds: this.multiItems.map(item => item.id)
        }).then((data) => {
          // go to do more where question submission message will display
          submissionStore.startFlow(
            this.currentSku,
            hostsConfig.siteKey,
            { page: 'do-more', props: { msgType: 'question' } }
          )
          submissionLoader.load()

          this.onQuestionSubmit(data.questionId)
          this.resetAfterSubmit()
        }).catch((e: RestError) => {
          this.onShowError(e)
        })
      } else {
        const qualifier = globalConfig.isAllowAnon() ? 'anonymous' : 'unconfirmed'
        createQuestionAnonOrUnconfirmedUserService.createQuestionAnonOrUnconfirmedUser({
          sku: this.currentSku,
          qualifier
        }, {
          questionType: 0,
          text: this.trimmedQuestionText,
          multiItemIds: this.multiItems.map(item => item.id)
        }).then((resp) => {
          if (resp.questionId) {
            this.onQuestionSubmit(resp.questionId)
          }
          this.resetAfterSubmit()
          if (globalConfig.ssoEnabled) {
            ssoService.ssoCreateQuestion(
              this.currentSku, resp.savedQuestionToken || resp.stagedQuestionToken)
          } else {
            // eslint-disable-next-line max-len
            import(/* webpackChunkName: 'registration-modal' */ 'components/registration/registration-modal-loader').then((registrationModalLoader) => {
              registrationModalLoader.default.load({
                // for anonymous, will be null for other qualifier types
                savedContentToken: resp.savedQuestionToken,
                // for emailRequired and emailConfirmRequired, will be null if for anonymous
                stagedContentToken: resp.stagedQuestionToken,
                registerFor: 'question'
              })
            })
          }
        }).catch((e: RestError) => {
          this.onShowError(e)
        })
      }
    },
    onInstantAnswerClick(itemClicked) {
      // if client provided an onInstantAnswerClick function
      const eventHandlers = window.turnToConfig.eventHandlers || {}
      if (eventHandlers.onInstantAnswerClick) {
        nextTick(() => {
          eventHandlers.onInstantAnswerClick()
        })
      }

      trackingService.queueEvent('instant-answers', 'result-click', this.currentSku, {
        id: itemClicked.id,
        type: itemClicked.ugcType
      })
    },
    onQuestionSubmit(questionId: number) {
      trackingService.queueEvent('instant-answers', 'submit', this.currentSku, { qId: questionId })
      // if client provided an onQuestionSubmit function
      const eventHandlers = window.turnToConfig.eventHandlers || {}
      if (eventHandlers.onQuestionSubmit) {
        eventHandlers.onQuestionSubmit({ id: questionId })
      }
    },
    queueWebEvents() {
      trackingService.queueEvent('instant-answers', 'load', this.currentSku)

      const rootElem = this.$el
      visibleObserver.register(rootElem, () => {
        trackingService.queueEvent('instant-answers', 'view', this.currentSku)
        visibleObserver.unregister(rootElem)
      })
    }
  },
  watch: {
    questionText() {
      this.askUpdate()
    },
    currentSku() {
      this.queueWebEvents()
    }
  },
  mounted() {
    if (this.currentSku) {
      this.queueWebEvents()
    }
  }
}
</script>

<style lang="scss" src="./instant-answers.scss"></style>
